import { useState, useRef, useEffect } from 'react';
import { Add, Close, Search } from '@mui/icons-material';
import useAddRequestStore from 'store/addRequestStore';
import { useGetBuyerProfiles } from 'hooks/usePropertyRequests';

export default function BuyerProfileSelector() {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [duplicateError, setDuplicateError] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    const { setRequestData, clearErrors, requestData } = useAddRequestStore();

    const { data: availableProfiles = [] } = useGetBuyerProfiles();

    const selectedProfile = requestData?.buyerProfile || '';

    const suggestions = availableProfiles.filter((profile: string) => profile.toLowerCase() !== selectedProfile.toLowerCase());

    const filteredProfiles = suggestions.filter((profile: string) => profile.toLowerCase().includes(searchQuery.toLowerCase()));

    const handleSelect = (profile: string) => {
        if (selectedProfile.toLowerCase() === profile.toLowerCase()) {
            setDuplicateError(`"${profile}" has already been selected.`);
            return;
        }
        setRequestData({ buyerProfile: profile });
        clearErrors('buyerProfile');
        setDuplicateError('');
        setIsOpen(false);
        setSearchQuery('');
    };

    const handleRemove = () => {
        setRequestData({
            buyerProfile: '',
        });
        setDuplicateError('');
    };

    // Close dropdown when clicking outside.
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <div className="w-full border bg-gray-50/10 rounded p-4">
            <div className="relative" ref={dropdownRef}>
                <button
                    onClick={() => {
                        setIsOpen(!isOpen);
                        setDuplicateError('');
                    }}
                    className="w-full px-4 lg:py-3 py-1.5 text-left text-gray-500 bg-white border rounded-lg focus:outline-none"
                >
                    Select Buyer Profile
                </button>
                {isOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg">
                        <div className="p-2 border-b">
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search Buyer Profiles"
                                    className="w-full pl-10 pr-4 lg:py-3 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setDuplicateError('');
                                    }}
                                />
                            </div>
                            {duplicateError && <div className="mt-1 text-sm text-red-500">{duplicateError}</div>}
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                            {filteredProfiles.map((profile: string) => (
                                <button
                                    key={profile}
                                    onClick={() => handleSelect(profile)}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none"
                                >
                                    {profile}
                                </button>
                            ))}
                            {filteredProfiles.length === 0 && searchQuery.trim() !== '' && (
                                <button
                                    onClick={() => handleSelect(searchQuery)}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none font-medium text-blue-600"
                                >
                                    Add Profile &quot;{searchQuery}&quot;
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {selectedProfile && (
                <div className="mt-4 w-full">
                    <div className="text-sm font-medium text-gray-500 mb-2.5">SELECTED</div>
                    <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-sm text-light-black">
                            {selectedProfile}
                            <button onClick={handleRemove} className="ml-2 focus:outline-none">
                                <Close className="h-4 w-4 text-light-black/40" />
                            </button>
                        </span>
                    </div>
                </div>
            )}

            {suggestions.length > 0 && (
                <div className="mt-4 w-full">
                    <div className="text-sm font-medium text-gray-500 mb-2.5">SUGGESTIONS</div>
                    <div className="flex flex-wrap gap-2">
                        {suggestions.slice(0, 3).map((profile: string) => (
                            <span key={profile} className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-sm text-light-black">
                                {profile}
                                <button onClick={() => handleSelect(profile)} className="ml-2 focus:outline-none">
                                    <Add className="h-4 w-4 text-my-blue" />
                                </button>
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
